/**
 * DemoOverlay.jsx - Guided Demo Overlay Component
 * 
 * PHASE H: Full Demo Mode Implementation
 * 
 * Provides:
 * - Step-by-step guided overlays
 * - Smooth animations
 * - Progress indicator
 * - Navigation controls
 * - Skip functionality
 * - Highlight targeting
 */

import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronRight, 
  ChevronLeft, 
  X, 
  Eye,
  Sliders,
  Activity,
  CheckCircle2,
  Play,
  SkipForward
} from 'lucide-react';
import { Button } from '../ui/button';
import { useDemoMode } from '../../contexts/DemoModeContext';

// Step icon mapping
const STEP_ICONS = {
  'welcome': Play,
  'observatory-intro': Eye,
  'observatory-insights': Eye,
  'observatory-scenario': Sliders,
  'simulation-intro': Sliders,
  'simulation-insight': Sliders,
  'mission-control-intro': Activity,
  'mission-control-health': Activity,
  'mission-control-integrity': Activity,
  'complete': CheckCircle2
};

// Position classes for overlay placement
const POSITION_CLASSES = {
  'center': 'fixed inset-0 flex items-center justify-center',
  'top': 'fixed top-24 left-1/2 -translate-x-1/2',
  'bottom': 'fixed bottom-24 left-1/2 -translate-x-1/2',
  'left': 'fixed top-1/2 left-8 -translate-y-1/2',
  'right': 'fixed top-1/2 right-8 -translate-y-1/2',
  'top-left': 'fixed top-24 left-8',
  'top-right': 'fixed top-24 right-8',
  'bottom-left': 'fixed bottom-24 left-8',
  'bottom-right': 'fixed bottom-24 right-8'
};

// Progress dots component
const ProgressDots = ({ current, total, onDotClick }) => {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }, (_, i) => (
        <button
          key={i}
          onClick={() => onDotClick(i)}
          className={`w-2 h-2 rounded-full transition-all ${
            i === current 
              ? 'bg-primary w-4' 
              : i < current 
                ? 'bg-primary/50' 
                : 'bg-muted-foreground/30'
          }`}
          aria-label={`Go to step ${i + 1}`}
        />
      ))}
    </div>
  );
};

// Main overlay component
export function DemoOverlay() {
  const {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    progress,
    isTransitioning,
    nextStep,
    prevStep,
    skipDemo,
    goToStep
  } = useDemoMode();

  const [highlightBox, setHighlightBox] = useState(null);
  const overlayRef = useRef(null);

  // Calculate highlight element position
  useEffect(() => {
    if (!isActive || !currentStep?.highlight || isTransitioning) {
      setHighlightBox(null);
      return;
    }

    const findElement = () => {
      try {
        const element = document.querySelector(currentStep.highlight);
        if (element) {
          const rect = element.getBoundingClientRect();
          setHighlightBox({
            top: rect.top - 8,
            left: rect.left - 8,
            width: rect.width + 16,
            height: rect.height + 16
          });
        } else {
          setHighlightBox(null);
        }
      } catch (err) {
        // Invalid selector - skip highlighting for this step
        console.warn('DemoOverlay: Invalid selector', currentStep.highlight, err);
        setHighlightBox(null);
      }
    };

    // Initial find
    findElement();

    // Re-find on scroll/resize
    const handleUpdate = () => findElement();
    window.addEventListener('scroll', handleUpdate);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isActive, currentStep, isTransitioning]);

  if (!isActive) return null;

  const StepIcon = STEP_ICONS[currentStep?.id] || Eye;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === totalSteps - 1;
  const isCenterPosition = currentStep?.position === 'center';

  return (
    <AnimatePresence mode="wait">
      {!isTransitioning && (
        <>
          {/* Background overlay */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/60 z-[100]"
            onClick={skipDemo}
          />

          {/* Highlight cutout */}
          {highlightBox && (
            <motion.div
              key="highlight"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed z-[101] pointer-events-none"
              style={{
                top: highlightBox.top,
                left: highlightBox.left,
                width: highlightBox.width,
                height: highlightBox.height,
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
                borderRadius: '12px',
                border: '2px solid rgba(59, 130, 246, 0.8)'
              }}
            >
              <motion.div
                className="absolute inset-0 rounded-xl"
                animate={{ 
                  boxShadow: [
                    '0 0 0 0 rgba(59, 130, 246, 0.4)',
                    '0 0 0 8px rgba(59, 130, 246, 0)',
                  ]
                }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
            </motion.div>
          )}

          {/* Content card */}
          <motion.div
            ref={overlayRef}
            key={`step-${currentStepIndex}`}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className={`z-[102] ${POSITION_CLASSES[currentStep?.position || 'center']}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className={`bg-card border border-border/50 rounded-2xl shadow-2xl ${
                isCenterPosition ? 'w-[500px] max-w-[90vw]' : 'w-[380px] max-w-[90vw]'
              }`}
              data-testid="demo-overlay-card"
            >
              {/* Header */}
              <div className="p-5 border-b border-border/50">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <StepIcon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">
                        {currentStep?.subtitle}
                      </p>
                      <h3 className="text-lg font-semibold text-foreground">
                        {currentStep?.title}
                      </h3>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={skipDemo}
                    className="text-muted-foreground hover:text-foreground -mr-2 -mt-1"
                    data-testid="demo-skip-button"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Body */}
              <div className="p-5 space-y-4">
                <p className="text-sm text-foreground leading-relaxed">
                  {currentStep?.description}
                </p>
                
                {currentStep?.instruction && (
                  <div className="p-3 rounded-lg bg-primary/5 border border-primary/10">
                    <p className="text-xs text-primary font-medium">
                      💡 {currentStep.instruction}
                    </p>
                  </div>
                )}

                {/* Progress */}
                <div className="pt-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground">
                      Step {currentStepIndex + 1} of {totalSteps}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {Math.round(progress)}% complete
                    </span>
                  </div>
                  <div className="h-1 bg-muted rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-primary rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-border/50 bg-muted/30 rounded-b-2xl">
                <div className="flex items-center justify-between">
                  <ProgressDots 
                    current={currentStepIndex} 
                    total={totalSteps}
                    onDotClick={goToStep}
                  />
                  
                  <div className="flex items-center gap-2">
                    {!isFirstStep && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={prevStep}
                        data-testid="demo-prev-button"
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Back
                      </Button>
                    )}
                    
                    {isLastStep ? (
                      <Button
                        size="sm"
                        onClick={skipDemo}
                        className="bg-primary text-primary-foreground"
                        data-testid="demo-finish-button"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Finish
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={nextStep}
                        className="bg-primary text-primary-foreground"
                        data-testid="demo-next-button"
                      >
                        {isFirstStep ? 'Start Tour' : 'Next'}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Skip hint */}
                <p className="text-[10px] text-muted-foreground text-center mt-3">
                  Press <kbd className="px-1.5 py-0.5 rounded bg-muted text-[9px] font-mono">Esc</kbd> to skip • 
                  <kbd className="px-1.5 py-0.5 rounded bg-muted text-[9px] font-mono ml-1">→</kbd> for next
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}

      {/* Transition indicator */}
      {isTransitioning && (
        <motion.div
          key="transition"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-center"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"
            />
            <p className="text-white text-sm">Navigating to next surface...</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default DemoOverlay;
